import type { StateCreator } from 'zustand';
import type { ClearablePatch, Stay } from '@my-fullstack-app/itinerary-types';
import { applyPatch, generateId } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';

/**
 * CRUD for `Trip.stays[]` — multi-day accommodation (hotels, Airbnb,
 * host stays) per ADR-0043. Stays live at trip level rather than day
 * level because a single Stay typically spans multiple days. Marks
 * 'stays' dirty (its own wire field, separate from 'days').
 */
export interface StaySlice {
  addStay: (
    tripId: string,
    stay: Omit<Stay, 'id'> & { id?: string | undefined},
  ) => void;
  removeStay: (tripId: string, stayId: string) => void;
  updateStay: (
    tripId: string,
    stayId: string,
    patch: ClearablePatch<Omit<Stay, 'id'>>,
  ) => void;
}

export const createStaySlice: StateCreator<ItineraryState, [], [], StaySlice> = (set, get) => ({
  addStay: (tripId, stayInput) => {
    get()._capture();
    const stay: Stay = { ...stayInput, id: stayInput.id ?? generateId() };
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return { ...t, stays: [...(t.stays ?? []), stay] };
      }),
    }));
    markTripDirty(tripId, ['stays']);
    track('stay.added', {
      tripId,
      hasBooking: !!stay.booking,
      hasCost: (stay.booking?.costs?.length ?? 0) > 0,
    });
  },

  removeStay: (tripId, stayId) => {
    get()._capture();
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return { ...t, stays: (t.stays ?? []).filter((stay) => stay.id !== stayId) };
      }),
    }));
    markTripDirty(tripId, ['stays']);
  },

  updateStay: (tripId, stayId, patch) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          stays: (t.stays ?? []).map((stay) =>
            stay.id === stayId ? applyPatch({ ...stay }, patch) : stay,
          ),
        };
      }),
    }));
    markTripDirty(tripId, ['stays']);
  },
});
